import {useEffect, useRef} from 'react';
import {io} from 'socket.io-client';
import {getAuthToken} from '../../services/authToken';
import {getSocketClientOptions, getSocketUrl} from '../../../../utils/socketConfig';

/**
 * Manages Socket.IO connection and room join for campaign state sync.
 */
export default function useCampaignSocketConnection({
  userAccountId,
  onConnect,
  onDisconnect,
  onConnectError,
  registerHandlers,
}) {
  const socketRef = useRef(null);
  const onConnectRef = useRef(onConnect);
  const onDisconnectRef = useRef(onDisconnect);
  const onConnectErrorRef = useRef(onConnectError);
  const registerHandlersRef = useRef(registerHandlers);

  onConnectRef.current = onConnect;
  onDisconnectRef.current = onDisconnect;
  onConnectErrorRef.current = onConnectError;
  registerHandlersRef.current = registerHandlers;

  useEffect(() => {
    if (!userAccountId) {
      return undefined;
    }

    const authToken = getAuthToken();
    if (!authToken) {
      return undefined;
    }

    const socketUrl = getSocketUrl();
    if (!socketUrl) {
      console.error('[useCampaignSocket] Socket URL is not configured');
      return undefined;
    }

    let cancelled = false;
    const socket = io(socketUrl, {
      ...getSocketClientOptions(authToken),
      forceNew: true,
    });
    socketRef.current = socket;

    const cleanupHandlers = registerHandlersRef.current(socket, () => cancelled);

    socket.on('connect', () => {
      if (cancelled) {
        return;
      }
      onConnectRef.current?.();
      socket.emit('join_user_account_room', {
        userAccountId,
        lang: localStorage.getItem('i18nextLng') || 'ptBR',
      });
    });

    socket.on('disconnect', () => {
      if (cancelled) {
        return;
      }
      onDisconnectRef.current?.();
    });

    socket.on('connect_error', (error) => {
      if (cancelled) {
        return;
      }
      onConnectErrorRef.current?.(error);
    });

    return () => {
      cancelled = true;
      cleanupHandlers?.();
      socket.removeAllListeners();
      socket.io.opts.reconnection = false;
      socket.close();
      if (socketRef.current === socket) {
        socketRef.current = null;
      }
    };
  }, [userAccountId]);

  return socketRef;
}
